import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import lukBg from "../../assets/newFace4.0.png";
import trailMaterialVert from "../shaders/someGlobalVert.glsl";
import trailMaterialFrag from "../shaders/trailMaterialFrag.glsl";
import lukaBgVert from "../shaders/lukaBgVert.glsl";
import lukaBgFrag from "../shaders/lukaBgFrag.glsl";
import blurLukaBgVert from "../shaders/someGlobalVert.glsl";
import blurLukaBgFrag from "../shaders/blurLukaBgFrag.glsl";

export function createScene({ renderer, camera, loadingManager } = {}) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x030303);

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  const isHighRes = window.innerWidth > 2500;

  const renderScale = isHighRes ? 0.75 : 1.0;

  const sizes = {
    width: Math.max(1, window.innerWidth),
    height: Math.max(1, window.innerHeight),
    pixelRatio: Math.min(window.devicePixelRatio || 1, 2.0) * renderScale,
  };

  composer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(sizes.pixelRatio);
  if (composer.setPixelRatio) {
    composer.setPixelRatio(sizes.pixelRatio);
  }

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isCoarse =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;
  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(ua) || isCoarse;

  const TRAIL_SCALE = isMobile ? 0.25 : isHighRes ? 0.35 : 0.5;
  const BLUR_SCALE = isMobile ? 0.5 : isHighRes ? 0.5 : 1.0;

  function createRT(width, height, scale = 1.0, options = {}) {
    const w = Math.max(1, Math.floor(width * sizes.pixelRatio * scale));
    const h = Math.max(1, Math.floor(height * sizes.pixelRatio * scale));

    const params = Object.assign(
      {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType,
        depthBuffer: false,
        stencilBuffer: false,
        generateMipmaps: false,
        wrapS: THREE.MirroredRepeatWrapping,
        wrapT: THREE.MirroredRepeatWrapping,
      },
      options,
    );

    const rt = new THREE.WebGLRenderTarget(w, h, params);
    rt.texture.colorSpace = THREE.NoColorSpace;
    rt._scale = scale;
    return rt;
  }

  let rtA = createRT(sizes.width, sizes.height, TRAIL_SCALE);
  let rtB = createRT(sizes.width, sizes.height, TRAIL_SCALE);

  let rtBlurA = createRT(sizes.width, sizes.height, BLUR_SCALE);
  let rtBlurB = createRT(sizes.width, sizes.height, BLUR_SCALE);

  const orthoScene = new THREE.Scene();
  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const quadGeo = new THREE.PlaneGeometry(2, 2);

  const trailUniforms = {
    prevTex: { value: null },
    cursor: { value: new THREE.Vector2(-1.0, -1.0) },
    prevCursor: { value: new THREE.Vector2(-1.0, -1.0) },
    resolution: {
      value: new THREE.Vector2(rtA.width, rtA.height),
    },
    decay: { value: isMobile ? 0.92 : 0.96 },
    splatRadius: { value: isMobile ? 0.04 : 0.06 },
    splatStrength: { value: isMobile ? 0.05 : 0.09 },
    advectStrength: { value: isMobile ? 0.12 : 0.2 },
    minDecay: { value: isMobile ? 0.8 : 0.97 },
    idleProgress: { value: 0.0 },
    time: { value: 0.0 },
    noiseScale: { value: isMobile ? 1.6 : 2.2 },
    tint: { value: new THREE.Vector3(0.95, 0.65, 0.45) },
  };

  const trailMaterial = new THREE.ShaderMaterial({
    uniforms: trailUniforms,
    vertexShader: trailMaterialVert,
    fragmentShader: trailMaterialFrag,
    depthWrite: false,
    transparent: true,
    blending: THREE.AdditiveBlending,
  });

  const quad = new THREE.Mesh(quadGeo, trailMaterial);
  orthoScene.add(quad);

  const textureLoader = new THREE.TextureLoader(loadingManager);
  let bgPlane = null;

  let resolveReady;
  const ready = new Promise((res) => (resolveReady = res));

  textureLoader.load(
    lukBg,
    (texture) => {
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = true;
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      texture.colorSpace = THREE.LinearSRGBColorSpace;
      texture.needsUpdate = true;

      const dist = camera.position.length();
      const fov = THREE.MathUtils.degToRad(camera.fov);
      const hWorld = 2 * dist * Math.tan(fov / 2);
      const unitsPerPixel = hWorld / sizes.height;

      const texW = texture.image.width || 1920;
      const texH = texture.image.height || 1080;
      const wWorld = texW * unitsPerPixel;
      const hWorldTex = texH * unitsPerPixel;

      const mat = new THREE.ShaderMaterial({
        uniforms: {
          bg: { value: texture },
          trail: { value: rtB.texture },
          cursor: { value: new THREE.Vector2(-1.0, -1.0) },
          distortion: { value: 0.36 },
          idleProgress: { value: 0.0 },
          time: { value: 0.0 },
          noiseScale: { value: 1.6 },
          uEyeCenter: { value: new THREE.Vector2(0.458, 0.6) },
          uEyeRadius: { value: 0.05 },
          uWinkVal: { value: 0.0 },
          uStrength: { value: 0.02 },
          uRatio: { value: texW / texH },
        },
        vertexShader: lukaBgVert,
        fragmentShader: lukaBgFrag,
        side: THREE.FrontSide,
        transparent: false,
      });

      bgPlane = new THREE.Mesh(
        new THREE.PlaneGeometry(wWorld, hWorldTex, 64, 48),
        mat,
      );
      bgPlane.userData = { texW, texH, geoBounds: { w: wWorld, h: hWorldTex } };
      bgPlane.frustumCulled = false;
      scene.add(bgPlane);

      resizeBgPlane();
      resolveReady();
    },
    undefined,
    (err) => {
      console.warn("startScene: background load error", err);
      resolveReady();
    },
  );

  function resizeBgPlane() {
    if (!bgPlane) return;

    const dist = Math.abs(camera.position.z - bgPlane.position.z);
    const vFov = THREE.MathUtils.degToRad(camera.fov);
    const visibleHeight = 2 * Math.tan(vFov / 2) * dist;
    const visibleWidth = visibleHeight * camera.aspect;

    const texW = bgPlane.userData.texW || 1920;
    const texH = bgPlane.userData.texH || 1080;
    const imageAspect = texW / texH;
    const viewAspect = visibleWidth / visibleHeight;

    let planeW = visibleWidth;
    let planeH = visibleHeight;

    if (viewAspect > imageAspect) {
      planeH = visibleWidth / imageAspect;
    } else {
      planeW = visibleHeight * imageAspect;
    }

    const geoBounds = bgPlane.userData.geoBounds || { w: texW, h: texH };
    bgPlane.scale.set(planeW / geoBounds.w, planeH / geoBounds.h, 1.0);
    bgPlane.position.set(0, 0, 0);
  }

  const targetCursor = new THREE.Vector2(-1.0, -1.0);
  const cursor = new THREE.Vector2(-1.0, -1.0);
  const prevCursor = new THREE.Vector2(-1.0, -1.0);
  const raycaster = new THREE.Raycaster();
  const pointerN = new THREE.Vector2();
  let lastMoveTime = performance.now();
  const idleThreshold = 900;
  let idleProgress = 0.0;
  const idleRiseSpeed = isMobile ? 0.0025 : 0.00018;
  const idleFallSpeed = isMobile ? 0.005 : 0.0035;

  function updateCursorFromClient(clientX, clientY) {
    lastMoveTime = performance.now();
    pointerN.x = (clientX / sizes.width) * 2 - 1;
    pointerN.y = -(clientY / sizes.height) * 2 + 1;

    if (bgPlane) {
      raycaster.setFromCamera(pointerN, camera);
      const intersects = raycaster.intersectObject(bgPlane, false);
      if (intersects.length > 0 && intersects[0].uv) {
        const uv = intersects[0].uv;
        targetCursor.set(uv.x, uv.y);
        return;
      }
    }

    targetCursor.set(clientX / sizes.width, 1 - clientY / sizes.height);
  }

  function handlePointerMove(e) {
    updateCursorFromClient(e.clientX, e.clientY);
  }

  let touchStartY = 0;
  function onTouchStart(e) {
    const t = e.touches[0];
    touchStartY = t.clientY;
    updateCursorFromClient(t.clientX, t.clientY);
    cursor.copy(targetCursor);
    prevCursor.copy(targetCursor);
    lastMoveTime = performance.now();
  }

  function onTouchMove(e) {
    const t = e.touches[0];
    const touchY = t.clientY;
    const delta = touchStartY - touchY;
    if (Math.abs(delta) > 20) {
      touchStartY = touchY;
    }
    updateCursorFromClient(t.clientX, t.clientY);
  }

  function onWheel(e) {
    if (Math.abs(e.deltaY) < 10) return;
  }

  function handleKeyDown(e) {
    if (e.key && e.key.toLowerCase() === "w") {
      triggerWink();
    }
  }

  function bindEvents() {
    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("keydown", handleKeyDown);
  }

  function unbindEvents() {
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("wheel", onWheel);
    window.removeEventListener("touchstart", onTouchStart);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("keydown", handleKeyDown);
  }

  const blurUniforms = {
    tDiffuse: { value: null },
    resolution: { value: new THREE.Vector2(rtBlurA.width, rtBlurA.height) },
    direction: { value: new THREE.Vector2(1.0 / rtBlurA.width, 0.0) },
    radius: { value: isMobile ? 1.5 : 3.0 },
  };

  const blurMaterial = new THREE.ShaderMaterial({
    uniforms: blurUniforms,
    vertexShader: blurLukaBgVert,
    fragmentShader: blurLukaBgFrag,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  });

  const blurQuad = new THREE.Mesh(quadGeo, blurMaterial);
  const blurScene = new THREE.Scene();
  const blurCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  blurScene.add(blurQuad);

  const quadScene = new THREE.Scene();
  const quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const grainUniforms = {
    tDiffuse: { value: null },
    time: { value: 0.0 },
    amount: { value: 0.4 },
    resolution: { value: new THREE.Vector2(sizes.width, sizes.height) },
    grainSize: { value: 1.2 },
    lumInfluence: { value: 0.7 },
  };

  const grainMaterial = new THREE.ShaderMaterial({
    uniforms: grainUniforms,
    vertexShader: `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`,
    fragmentShader: `
  precision highp float;
  uniform sampler2D tDiffuse;
  uniform float time;
  uniform float amount;
  uniform vec2 resolution;
  uniform float grainSize;
  uniform float lumInfluence;
  varying vec2 vUv;

  float rand(vec2 n) { 
      return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
  }
  float organicNoise(vec2 uv, float timeStep) {
      float n1 = rand(uv + vec2(timeStep * 0.013, timeStep * 0.071));
      float n2 = rand(uv * 0.7 + vec2(timeStep * 0.064, timeStep * 0.021));
      return (n1 + n2) * 0.5;
  }
  float overlay(float base, float blend) {
      return (base < 0.5) ? (2.0 * base * blend) : (1.0 - 2.0 * (1.0 - base) * (1.0 - blend));
  }
  vec3 blendOverlay(vec3 base, vec3 blend) {
      return vec3(overlay(base.r, blend.r), overlay(base.g, blend.g), overlay(base.b, blend.b));
  }

  void main() {
    vec4 color = texture2D(tDiffuse, vUv);
    float timeStep = floor(time * 24.0); 
    vec2 grainCoord = gl_FragCoord.xy / grainSize;
    float noiseValue = organicNoise(grainCoord, timeStep);
    float blendNoise = 0.5 + (noiseValue - 0.5) * amount;
    vec3 overlayColor = blendOverlay(color.rgb, vec3(blendNoise));
    float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
    float noiseMask = pow(luminance, 0.5) * (1.0 - luminance);
    float finalBlend = mix(1.0, noiseMask, lumInfluence);
    vec3 finalColor = mix(color.rgb, overlayColor, finalBlend);
    gl_FragColor = vec4(finalColor, color.a);
  }
`,
    depthTest: false,
    depthWrite: false,
    transparent: false,
  });

  const grainQuad = new THREE.Mesh(quadGeo, grainMaterial);
  quadScene.add(grainQuad);

  const grainPass = {
    enabled: true,
    renderToScreen: true,
    setSize(width, height) {
      grainUniforms.resolution.value.set(width, height);
    },
    render(rendererRef, writeBuffer, readBuffer, delta) {
      grainUniforms.tDiffuse.value = readBuffer.texture;
      grainUniforms.time.value += delta;
      if (this.renderToScreen) {
        rendererRef.setRenderTarget(null);
        rendererRef.render(quadScene, quadCamera);
      } else {
        rendererRef.setRenderTarget(writeBuffer);
        rendererRef.clear();
        rendererRef.render(quadScene, quadCamera);
      }
    },
  };

  composer.addPass(grainPass);

  function setRTSizeSafe(rt, width, height) {
    if (!rt) return;
    const w = Math.max(
      1,
      Math.floor(width * sizes.pixelRatio * (rt._scale || 1)),
    );
    const h = Math.max(
      1,
      Math.floor(height * sizes.pixelRatio * (rt._scale || 1)),
    );
    if (rt.width !== w || rt.height !== h) {
      rt.setSize(w, h);
    }
  }

  function triggerWink() {
    if (!bgPlane || !bgPlane.material || !bgPlane.material.uniforms) return;
    const uniforms = bgPlane.material.uniforms;
    const start = performance.now();
    const duration = 300;
    function animate() {
      const p = (performance.now() - start) / duration;
      if (p < 1) {
        uniforms.uWinkVal.value = Math.sin(p * Math.PI);
        requestAnimationFrame(animate);
      } else {
        uniforms.uWinkVal.value = 0;
      }
    }
    animate();
  }

  let elapsed = 0;

  function update(dt = 1 / 60) {
    elapsed += dt;
    const now = performance.now();
    prevCursor.copy(cursor);
    const smoothing = 5.0;
    const followLerp = 1.0 - Math.exp(-smoothing * dt);
    cursor.lerp(targetCursor, followLerp);
    trailUniforms.cursor.value.copy(cursor);
    trailUniforms.prevCursor.value.copy(prevCursor);

    const speed = cursor.distanceTo(prevCursor) * sizes.width;
    const speedFactor = Math.min(1.0, speed * 80.0);
    const radiusPx = isMobile ? 6 : 12;
    const baseRadius = radiusPx / sizes.height;

    const rtScaleCompensation = 1.0 / (rtA._scale || 1);
    trailUniforms.splatRadius.value = baseRadius * rtScaleCompensation;
    trailUniforms.splatStrength.value = 0.03 + 0.2 * (0.5 + speedFactor * 0.2);
    trailUniforms.noiseScale.value = 1.8 + 1.2 * speedFactor;
    trailUniforms.time.value = now * 0.001;

    if (isMobile) {
      trailUniforms.splatRadius.value *= 0.4;
      trailUniforms.splatStrength.value = 0.1 + 0.2 * (0.5 + speedFactor * 0.2);
      trailUniforms.decay.value = 0.8;
      trailUniforms.minDecay.value = 0.8;
    }

    const deltaMs = now - lastMoveTime;
    if (deltaMs >= idleThreshold) {
      idleProgress = Math.min(1.0, idleProgress + idleRiseSpeed * dt * 1000);
    } else {
      idleProgress = Math.max(0.0, idleProgress - idleFallSpeed * dt * 1000);
    }
    trailUniforms.idleProgress.value = idleProgress;

    trailUniforms.prevTex.value = rtB.texture;
    trailUniforms.resolution.value.set(rtA.width, rtA.height);

    renderer.setRenderTarget(rtA);
    renderer.clear();
    renderer.render(orthoScene, orthoCamera);
    [rtA, rtB] = [rtB, rtA];

    blurUniforms.tDiffuse.value = rtB.texture;
    blurUniforms.resolution.value.set(rtBlurA.width, rtBlurA.height);

    blurUniforms.direction.value.set(1.0 / rtBlurA.width, 0.0);
    renderer.setRenderTarget(rtBlurA);
    renderer.clear();
    renderer.render(blurScene, blurCamera);

    blurUniforms.tDiffuse.value = rtBlurA.texture;
    blurUniforms.direction.value.set(0.0, 1.0 / rtBlurB.height);
    renderer.setRenderTarget(rtBlurB);
    renderer.clear();
    renderer.render(blurScene, blurCamera);

    trailUniforms.prevTex.value = rtBlurB.texture;

    renderer.setRenderTarget(null);

    if (bgPlane && bgPlane.material && bgPlane.material.uniforms) {
      const u = bgPlane.material.uniforms;
      if (u.cursor) u.cursor.value.copy(cursor);
      if (u.trail) u.trail.value = rtBlurB.texture;
      if (u.idleProgress) u.idleProgress.value = idleProgress;
      if (u.time) u.time.value = now * 0.001;
    }
  }

  function renderForSnapshot(rendererRef, targetRT, cam = camera) {
    update(1 / 60);
    const prevRT = rendererRef.getRenderTarget();
    rendererRef.setRenderTarget(targetRT);
    rendererRef.clear(true, true, true);
    rendererRef.render(scene, cam);
    rendererRef.setRenderTarget(prevRT);
  }

  function hideLoader() {
    const loaderEl =
      typeof document !== "undefined" && document.getElementById("loader");
    if (loaderEl) loaderEl.style.display = "none";
  }

  function onResize(w, h) {
    const width = Math.max(1, w);
    const height = Math.max(1, h);

    sizes.width = width;
    sizes.height = height;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    setRTSizeSafe(rtA, width, height);
    setRTSizeSafe(rtB, width, height);
    setRTSizeSafe(rtBlurA, width, height);
    setRTSizeSafe(rtBlurB, width, height);

    trailUniforms.resolution.value.set(rtA.width, rtA.height);
    blurUniforms.resolution.value.set(rtBlurA.width, rtBlurA.height);
    grainUniforms.resolution.value.set(width, height);

    composer.setSize(width, height);

    if (bgPlane && bgPlane.userData) {
      resizeBgPlane();
    }
  }

  function activate() {
    bindEvents();
  }

  function deactivate() {
    unbindEvents();
  }

  function dispose() {
    quadGeo.dispose();
    trailMaterial.dispose();
    blurMaterial.dispose();
    grainMaterial.dispose();
    if (rtA) rtA.dispose();
    if (rtB) rtB.dispose();
    if (rtBlurA) rtBlurA.dispose();
    if (rtBlurB) rtBlurB.dispose();
    if (bgPlane) {
      if (bgPlane.geometry) bgPlane.geometry.dispose();
      if (bgPlane.material) {
        if (bgPlane.material.uniforms?.bg?.value) {
          bgPlane.material.uniforms.bg.value.dispose();
        }
        bgPlane.material.dispose();
      }
    }
  }

  onResize(sizes.width, sizes.height);

  return {
    scene,
    composer,
    update,
    activate,
    deactivate,
    dispose,
    ready,
    triggerWink,
    hideLoader,
    renderForSnapshot,
    onResize,
  };
}
